import { jsPDF } from "jspdf";

type Align = "left" | "center" | "right";

export type TableColumn<T> = {
  header: string;
  field?: keyof T;
  getValue?: (row: T) => unknown;
  widthMm?: number;
  align?: Align;
};

export type ItemsTablePdfOptions = {
  title?: string;
  fileName?: string;
  marginMm?: number;
  fontSize?: number;
  headerFontSize?: number;
  lineHeightMm?: number;
  headerHeightMm?: number;
  rowPaddingMm?: number;
  zebra?: boolean;
  dateFormatter?: (iso: string) => string;

  logoDataUrl?: string;
  logoFormat?: "PNG" | "JPEG" | "WEBP";
  logoWidthMm?: number;
  logoHeightMm?: number;
  logoGapMm?: number;

  userName?: string;
  printedAtFormatter?: (d: Date) => string;
};

const defaultDateFormatter = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export const defaultPrintedAtFormatter = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export async function urlToDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  const blob = await res.blob();

  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

export function exportItemsToTablePdf<T extends Record<string, any>>(
  items: T[],
  columns: TableColumn<T>[],
  opts: ItemsTablePdfOptions = {}
) {
  const {
    title = "Inventura",
    fileName = "Inventura.pdf",
    marginMm = 10,
    fontSize = 9,
    headerFontSize = 10,
    lineHeightMm = 4.2,
    headerHeightMm = 7,
    rowPaddingMm = 1.5,
    zebra = true,
    dateFormatter = defaultDateFormatter,
  } = opts;

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const usableW = pageW - 2 * marginMm;

  const fixedSum = columns.reduce((s, c) => s + (c.widthMm ?? 0), 0);
  const flexCols = columns.filter((c) => !c.widthMm).length;
  const flexW = flexCols > 0 ? Math.max(10, (usableW - fixedSum) / flexCols) : 0;

  const colWidths = columns.map((c) => c.widthMm ?? flexW);

  const drawTitle = () => {
    doc.setFontSize(14);

    const titleY = marginMm;

    const {
      logoDataUrl,
      logoFormat = "PNG",
      logoWidthMm = 10,
      logoHeightMm = 10,
      logoGapMm = 3,
      userName,
      printedAtFormatter = defaultPrintedAtFormatter,
    } = opts;

    let titleX = marginMm;

    if (logoDataUrl) {
      const logoX = marginMm - 2;
      const logoY = titleY - logoHeightMm + 9;

      doc.addImage(logoDataUrl, logoFormat, logoX, logoY, logoWidthMm, logoHeightMm);
      titleX = marginMm + logoWidthMm + logoGapMm;
    }

    doc.text(title, titleX, titleY);

    const rightX = pageW - marginMm;
    const now = new Date();
    const printed = printedAtFormatter(now);

    doc.setFontSize(7);

    const metaText = userName
      ? `${userName}, ${printed}`
      : printed;

    doc.text(metaText, rightX, titleY, { align: "right" });
    doc.setFontSize(fontSize);
  };


  const drawHeader = (y: number) => {
    doc.setDrawColor(60);
    doc.setLineWidth(0.2);

    let x = marginMm;

    doc.setFontSize(headerFontSize);

    for (let i = 0; i < columns.length; i++) {
      const w = colWidths[i];
      const text = columns[i].header ?? "";
      const align = columns[i].align ?? "left";

      doc.rect(x, y, w, headerHeightMm, "S");

      const tx =
        align === "left" ? x + 1.5 : align === "right" ? x + w - 1.5 : x + w / 2;
      const ty = y + headerHeightMm / 2 + 2.2;
      doc.text(String(text), tx, ty, { align });

      x += w;
    }

    return y + headerHeightMm;
  };

  const getCellText = (col: TableColumn<T>, row: T) => {
    const raw =
      col.getValue?.(row) ?? (col.field ? (row as any)[col.field] : "");
    if (raw == null) return "";

    if (col.field === "inventoryDate" && typeof raw === "string") {
      return dateFormatter(raw);
    }

    return String(raw);
  };

  const measureRowHeight = (row: T) => {
    doc.setFontSize(fontSize);
    let maxLines = 1;

    for (let i = 0; i < columns.length; i++) {
      const w = colWidths[i];
      const text = getCellText(columns[i], row);
      const lines = doc.splitTextToSize(text, Math.max(1, w - 3));
      maxLines = Math.max(maxLines, lines.length);
    }

    return maxLines * lineHeightMm + rowPaddingMm * 2;
  };

  const drawRow = (row: T, y: number, rowIndex: number) => {
    doc.setFontSize(fontSize);

    const rowH = measureRowHeight(row);

    let x = marginMm;
    doc.setDrawColor(80);
    doc.setLineWidth(0.2);

    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const w = colWidths[i];
      const align = col.align ?? "left";

      doc.rect(x, y, w, rowH, "S");

      const text = getCellText(col, row);
      const lines = doc.splitTextToSize(text, Math.max(1, w - 3));

      const tx =
        align === "left" ? x + 1.5 : align === "right" ? x + w - 1.5 : x + w / 2;
      let ty = y + rowPaddingMm + lineHeightMm;

      for (const line of lines) {
        doc.text(String(line), tx, ty, { align });
        ty += lineHeightMm;
      }

      x += w;
    }

    return y + rowH;
  };

  drawTitle();
  let cursorY = marginMm + 8;

  cursorY = drawHeader(cursorY);

  for (let i = 0; i < items.length; i++) {
    const rowH = measureRowHeight(items[i]);

    if (cursorY + rowH > pageH - marginMm) {
      doc.addPage();
      drawTitle();
      cursorY = marginMm + 8;
      cursorY = drawHeader(cursorY);
    }

    cursorY = drawRow(items[i], cursorY, i);
  }

  doc.save(fileName);
}